import { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import TableTag from '@components/table_tag';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import {
    ETransactionAction,
    ETransactionStatus,
    ETransactionType,
    TTransactionsFilter
} from '@xeppt/xeppt-sdk/types/transaction';
import {
    transactionsActionsFilterArray,
    transactionsMethodsFilterArray
} from '@const/transactions';
import { getDisableFeature } from '@utils/index';
import RangeDatePicker from '@components/range_date_picker';
import { ETransactionMethod } from '@xeppt/xeppt-sdk/types';

const cx = classNames.bind(styles);

interface IProps {
    toggleFilter: () => void;
    onChangeFilter: (val: TTransactionsFilter) => void;
    activeTab?: string;
    triggerClearFilter: number;
    isSale?: boolean;
    initialDateFrom?: string | null;
    initialDateTo?: string | null;
}

const FilterSection: FC<IProps> = ({
    toggleFilter,
    onChangeFilter,
    activeTab,
    isSale,
    triggerClearFilter,
    initialDateFrom,
    initialDateTo
}) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'transactions.filter'
    });
    const { labelLocale } = useLocales();
    const [dateFilter, setDateFilter] = useState('');
    const [date_from, setDateFrom] = useState<string | null>(initialDateFrom ?? null);
    const [date_to, setDateTo] = useState<string | null>(initialDateTo ?? null);
    const [typeFilter, setTypeFilter] = useState<ETransactionAction[]>([]);
    const [method, setMethod] = useState<ETransactionMethod[]>([]);
    const [activeDirection, setActionDirection] = useState<ETransactionType | null>(null);
    const [statusFilter, setStatusFilter] = useState<ETransactionStatus[]>([]);
    const [saleOrder, setSaleOrder] = useState<'ASC' | 'DESC'>('DESC');

    const handleChangeType = (value: ETransactionAction) => {
        if (typeFilter.includes(value)) {
            setTypeFilter((state) => state.filter((item) => item !== value));
        } else {
            setTypeFilter((state) => [...state, value]);
        }
    };

    const handleChangeMethod = (value: ETransactionMethod) => {
        if (method.includes(value)) {
            setMethod((state) => state.filter((item) => item !== value));
        } else {
            setMethod((state) => [...state, value]);
        }
    };

    const handleChangeStatus = (value: ETransactionStatus) => {
        if (statusFilter.includes(value)) {
            setStatusFilter((state) => state.filter((item) => item !== value));
        } else {
            setStatusFilter((state) => [...state, value]);
        }
    };

    useEffect(() => {
        if (triggerClearFilter > 0) {
            setTypeFilter([]);
            setMethod([]);
            setActionDirection(null);
            setStatusFilter([]);
            setSaleOrder('DESC');
            setDateFilter('');
            setDateFrom(null);
            setDateTo(null);
        }
    }, [triggerClearFilter]);

    useEffect(() => {
        const directionArr = activeDirection !== null ? [activeDirection] : [];
        const saleParams = isSale ? { status: statusFilter, order: saleOrder } : {};
        if (date_to && date_from) {
            onChangeFilter({
                action: typeFilter,
                date_to: new Date(date_to),
                date_from: new Date(date_from),
                method,
                type: directionArr,
                ...saleParams
            });
        } else {
            onChangeFilter({
                action: typeFilter,
                method,
                type: directionArr,
                ...saleParams
            });
        }
    }, [dateFilter, typeFilter, activeDirection, date_from, date_to, method, statusFilter, saleOrder]);

    const transactionsActionsFilter = useMemo(() => {
        return transactionsActionsFilterArray(t);
    }, []);

    return (
        <div className={styles.wrapper}>
            <div className={styles.title_wrapper}>
                <Typography variant="h4">Filters</Typography>
                <button onClick={toggleFilter}>
                    <Icon name="close" />
                </button>
            </div>
            <div className={styles.block_wrapper}>
                <RangeDatePicker
                    values={[date_from, date_to]}
                    onChange={(val) => {
                        setDateFrom(val?.[0]);
                        setDateTo(val?.[1]);
                        setDateFilter('');
                    }}
                    maxDate={getDisableFeature()}
                    isDisableFeature
                    full
                    label={labelLocale('date_range')}
                />
            </div>
            {activeTab === 'transactions' && (
                <>
                    {isSale && (
                        <>
                            <div className={styles.block_wrapper}>
                                <Typography variant="body3" weight="semibold">
                                    Money movement
                                </Typography>
                                <div className={styles.radio_group}>
                                    <button
                                        type="button"
                                        className={cx('radio_option', 'radio_option_right', {
                                            radio_active: activeDirection === null
                                        })}
                                        onClick={() => setActionDirection(null)}>
                                        <span className={styles.direction_icon}>
                                            <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9.5 22V8" stroke="#282828" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M5 12.5L9.5 8L14 12.5" stroke="#282828" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M20.5 8V22" stroke="#282828" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M16 17.5L20.5 22L25 17.5" stroke="#282828" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        All
                                    </button>
                                    <button
                                        type="button"
                                        className={cx('radio_option', 'radio_option_right', {
                                            radio_active:
                                                activeDirection === ETransactionType.DEBIT
                                        })}
                                        onClick={() => setActionDirection(ETransactionType.DEBIT)}>
                                        <span className={styles.direction_icon}>
                                            <Icon name="transaction_arrow_down" />
                                        </span>
                                        Money in
                                    </button>
                                    <button
                                        type="button"
                                        className={cx('radio_option', 'radio_option_right', {
                                            radio_active:
                                                activeDirection === ETransactionType.CREDIT
                                        })}
                                        onClick={() =>
                                            setActionDirection(ETransactionType.CREDIT)
                                        }>
                                        <span className={styles.direction_icon}>
                                            <Icon name="transaction_arrow_up" />
                                        </span>
                                        Money out
                                    </button>
                                </div>
                            </div>
                            <div className={styles.block_wrapper}>
                                <Typography variant="body3" weight="semibold">
                                    Status
                                </Typography>
                                <div className={styles.tags_wrapper}>
                                    <TableTag
                                        isActive={statusFilter.includes(
                                            ETransactionStatus.APPROVED
                                        )}
                                        onClick={() =>
                                            handleChangeStatus(ETransactionStatus.APPROVED)
                                        }>
                                        Approved
                                    </TableTag>
                                    <TableTag
                                        isActive={statusFilter.includes(
                                            ETransactionStatus.PENDING
                                        )}
                                        onClick={() =>
                                            handleChangeStatus(ETransactionStatus.PENDING)
                                        }>
                                        Pending
                                    </TableTag>
                                    <TableTag
                                        isActive={statusFilter.includes(
                                            ETransactionStatus.CANCELED
                                        )}
                                        onClick={() =>
                                            handleChangeStatus(ETransactionStatus.CANCELED)
                                        }>
                                        Canceled
                                    </TableTag>
                                    <TableTag
                                        isActive={statusFilter.includes(
                                            ETransactionStatus.DECLINED
                                        )}
                                        onClick={() =>
                                            handleChangeStatus(ETransactionStatus.DECLINED)
                                        }>
                                        Declined
                                    </TableTag>
                                </div>
                            </div>
                            <div className={styles.block_wrapper}>
                                <Typography variant="body3" weight="semibold">
                                    Sort by
                                </Typography>
                                <div className={styles.radio_group}>
                                    <button
                                        type="button"
                                        className={cx('radio_option', {
                                            radio_active: saleOrder === 'DESC'
                                        })}
                                        onClick={() => setSaleOrder('DESC')}>
                                        Date: Newest first
                                    </button>
                                    <button
                                        type="button"
                                        className={cx('radio_option', {
                                            radio_active: saleOrder === 'ASC'
                                        })}
                                        onClick={() => setSaleOrder('ASC')}>
                                        Date: Oldest first
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                    {!isSale && (
                        <div className={styles.block_wrapper}>
                            <Typography variant="body3" weight="semibold">
                                Method
                            </Typography>
                            <div className={styles.tags_wrapper}>
                                {transactionsMethodsFilterArray(t).map((item) => {
                                    return (
                                        <TableTag
                                            key={item.method}
                                            isActive={method.includes(item.method)}
                                            onClick={() => handleChangeMethod(item.method)}>
                                            {item.label}
                                        </TableTag>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {!isSale && (
                        <div className={styles.block_wrapper}>
                            <Typography variant="body3" weight="semibold">
                                Action
                            </Typography>
                            <div className={styles.tags_wrapper}>
                                {transactionsActionsFilter.map((item) => {
                                    return (
                                        <TableTag
                                            key={item.action}
                                            isActive={typeFilter.includes(item.action)}
                                            onClick={() => handleChangeType(item.action)}>
                                            {item.label}
                                        </TableTag>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FilterSection;
